import React, { useState } from 'react';
import { Button, Typography, Space, Row, Col, Card, Avatar, Rate } from 'antd';
import { 
  CheckOutlined, 
  MessageOutlined,
  ShopOutlined,
  BarChartOutlined,
  WhatsAppOutlined,
  ShoppingCartOutlined,
  ApiOutlined,
  TruckOutlined,
  UserOutlined,
  DashboardOutlined,
  MenuOutlined,
  CloseOutlined
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { ChevronRightIcon, StarIcon } from '@heroicons/react/24/outline';
import logo from '../assets/logo.png';
import './LandingPage.css';

const { Title, Paragraph, Text } = Typography;

const LandingPage = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  return (
    <div className="landing-root">
      {/* Header moderne avec système de couleurs cohérent */}
      <header className="landing-header">
        <div className="landing-header-inner">
          {/* Logo */}
          <div className="landing-logo">
            <div className="landing-logo-icon">
              <img src={logo} alt="Mercuri Shop" style={{ width: '24px', height: '24px', objectFit: 'contain' }} />
            </div>
            <Text className="landing-logo-text">Mercuri Shop</Text>
          </div>

          {/* Navigation Desktop */}
          <nav className="landing-nav landing-nav-desktop">
            <a href="#fonctionnalites" className="landing-nav-link">Fonctionnalités</a>
            <a href="#integrations" className="landing-nav-link">Intégrations</a>
            <a href="#temoignages" className="landing-nav-link">Témoignages</a>
            <Link to="/pricing" className="landing-nav-link">Tarifs</Link>
          </nav>

          {/* CTA Buttons Desktop avec nouvelles couleurs */}
          <div className="landing-header-actions landing-header-actions-desktop">
            <Button 
              type="primary" 
              className="header-cta-primary"
            >
              Demander une démo
              <ChevronRightIcon className="h-4 w-4 ml-1" />
            </Button>
            <Link to="/login">
              <Button 
                className="header-cta-secondary"
              >
                Se connecter
              </Button>
            </Link>
          </div>

          {/* Menu Hamburger Mobile */}
          <div className="landing-header-mobile-toggle">
            <Button 
              icon={mobileMenuOpen ? <CloseOutlined /> : <MenuOutlined />}
              onClick={toggleMobileMenu}
              type="text"
              className="mobile-menu-toggle"
            />
          </div>
        </div>

        {/* Menu Mobile Dropdown */}
        {mobileMenuOpen && (
          <div className="landing-header-mobile-menu">
            <nav className="landing-nav-mobile">
              <a href="#fonctionnalites" className="landing-nav-link-mobile" onClick={toggleMobileMenu}>
                Fonctionnalités
              </a>
              <a href="#integrations" className="landing-nav-link-mobile" onClick={toggleMobileMenu}>
                Intégrations
              </a>
              <a href="#temoignages" className="landing-nav-link-mobile" onClick={toggleMobileMenu}>
                Témoignages
              </a>
              <Link to="/pricing" className="landing-nav-link-mobile" onClick={toggleMobileMenu}>
                Tarifs
              </Link>
            </nav>
            
            <div className="landing-header-actions-mobile">
              <Button 
                type="primary" 
                block
                className="header-cta-primary-mobile"
                onClick={toggleMobileMenu}
              >
                Demander une démo
              </Button>
              <Link to="/login">
                <Button 
                  block
                  className="header-cta-secondary-mobile"
                  onClick={toggleMobileMenu}
                >
                  Se connecter
                </Button>
              </Link>
            </div>
          </div>
        )}
      </header>

      {/* Hero Section avec design moderne */}
      <section className="landing-hero">
        <div className="landing-hero-container">
          <Row gutter={[48, 48]} align="middle">
            <Col xs={24} lg={12}>
              <Space direction="vertical" size={32} className="landing-hero-content">
                <Title level={1} className="landing-hero-title">
                  Automatisez votre business avec{' '}
                  <span className="landing-hero-title-highlight">WhatsApp</span>
                </Title>
                
                <Paragraph className="landing-hero-description">
                  Mercuri Shop connecte votre boutique Shopify directement à WhatsApp, automatise la gestion des commandes et la communication client pour une efficacité maximale et des clients plus satisfaits.
                </Paragraph>

                {/* Features List avec nouvelles couleurs */}
                <div className="landing-hero-features">
                  <Space direction="vertical" size={16}>
                    <div className="landing-hero-feature">
                      <CheckOutlined className="feature-check-icon" />
                      <Text className="landing-hero-feature-text">
                        Notifications WhatsApp en temps réel pour les commandes
                      </Text>
                    </div>
                    <div className="landing-hero-feature">
                      <CheckOutlined className="feature-check-icon" />
                      <Text className="landing-hero-feature-text">
                        Automatisation complète des messages clients
                      </Text>
                    </div>
                    <div className="landing-hero-feature">
                      <CheckOutlined className="feature-check-icon" />
                      <Text className="landing-hero-feature-text">
                        Intégration Shopify et gestion multi-boutiques
                      </Text>
                    </div>
                  </Space>
                </div>

                {/* CTA Buttons avec design moderne */}
                <div className="landing-hero-buttons">
                  <Link to="/register">
                    <Button 
                      type="primary" 
                      size="large"
                      className="landing-hero-cta-primary"
                    >
                      Essai gratuit 14 jours
                      <ChevronRightIcon className="h-5 w-5 ml-2" />
                    </Button>
                  </Link>
                  <Link to="/contact">
                    <Button 
                      size="large"
                      className="landing-hero-cta-secondary"
                    >
                      Demander une démo
                    </Button>
                  </Link>
                </div>

                {/* Social Proof avec avatars modernisés */}
                <div className="landing-hero-social-proof">
                  <div className="landing-hero-avatars">
                    <Avatar size={40} src={`data:image/svg+xml;base64,${btoa(`
                      <svg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="20" cy="20" r="20" fill="#2563eb"/>
                        <circle cx="20" cy="16" r="6" fill="white"/>
                        <path d="M8 32c0-8 5.5-12 12-12s12 4 12 12" fill="white"/>
                      </svg>
                    `)}`} className="landing-hero-avatar" />
                    <Avatar size={40} src={`data:image/svg+xml;base64,${btoa(`
                      <svg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="20" cy="20" r="20" fill="#0891b2"/>
                        <circle cx="20" cy="16" r="6" fill="white"/>
                        <path d="M8 32c0-8 5.5-12 12-12s12 4 12 12" fill="white"/>
                      </svg>
                    `)}`} className="landing-hero-avatar" />
                    <Avatar size={40} src={`data:image/svg+xml;base64,${btoa(`
                      <svg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="20" cy="20" r="20" fill="#0e7490"/>
                        <circle cx="20" cy="16" r="6" fill="white"/>
                        <path d="M8 32c0-8 5.5-12 12-12s12 4 12 12" fill="white"/>
                      </svg>
                    `)}`} className="landing-hero-avatar" />
                  </div>
                  <Text className="landing-hero-social-text">
                    Plus de 500 boutiques font confiance à Mercuri Shop
                  </Text>
                </div>
              </Space>
            </Col>
            
            <Col xs={24} lg={12}>
              <div className="landing-hero-image">
                <div className="landing-hero-mockup" style={{
                  background: `url("data:image/svg+xml;base64,${btoa(`
                    <svg width="420" height="520" viewBox="0 0 420 520" xmlns="http://www.w3.org/2000/svg">
                      <rect width="420" height="520" rx="16" fill="#F8FAFC"/>
                      <rect x="20" y="20" width="380" height="60" rx="8" fill="#2563eb"/>
                      <text x="210" y="50" text-anchor="middle" fill="white" font-family="Arial, sans-serif" font-size="16" font-weight="bold">Mercuri Shop Dashboard</text>
                      <rect x="20" y="100" width="180" height="120" rx="8" fill="white" stroke="#E5E7EB"/>
                      <text x="110" y="140" text-anchor="middle" fill="#6B7280" font-family="Arial, sans-serif" font-size="12">Commandes</text>
                      <text x="110" y="180" text-anchor="middle" fill="#111827" font-family="Arial, sans-serif" font-size="24" font-weight="bold">247</text>
                      <rect x="220" y="100" width="180" height="120" rx="8" fill="white" stroke="#E5E7EB"/>
                      <text x="310" y="140" text-anchor="middle" fill="#6B7280" font-family="Arial, sans-serif" font-size="12">Messages</text>
                      <text x="310" y="180" text-anchor="middle" fill="#111827" font-family="Arial, sans-serif" font-size="24" font-weight="bold">1,524</text>
                      <rect x="20" y="240" width="380" height="260" rx="8" fill="white" stroke="#E5E7EB"/>
                      <text x="210" y="270" text-anchor="middle" fill="#111827" font-family="Arial, sans-serif" font-size="14" font-weight="bold">Messages WhatsApp</text>
                    </svg>
                  `)}")`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center'
                }} />
              </div>
            </Col>
          </Row>
        </div>
      </section>

      {/* Features Section avec couleurs modernisées */}
      <section id="fonctionnalites" className="landing-features">
        <div className="landing-features-container">
          {/* Section Header */}
          <div className="landing-features-header">
            <Title level={2} className="landing-features-title">
              Automatisation sans effort, résultats puissants
            </Title>
            <Paragraph className="landing-features-description">
              Mercuri Shop centralise toutes vos commandes et conversations clients en un seul endroit, vous permettant d'automatiser, gérer et satisfaire chaque client sans le chaos.
            </Paragraph>
          </div>

          {/* Features Cards */}
          <Row gutter={[32, 32]}>
            <Col xs={24} md={8}>
              <Card className="landing-feature-card">
                <div className="landing-feature-icon">
                  <ShoppingCartOutlined className="feature-icon" />
                </div>
                <Title level={4} className="landing-feature-title">
                  Gestion automatisée des commandes
                </Title>
                <Paragraph className="landing-feature-text">
                   Notifiez automatiquement vos clients via WhatsApp à chaque changement de statut de commande.
                </Paragraph>
              </Card>
            </Col>

            <Col xs={24} md={8}>
              <Card className="landing-feature-card">
                <div className="landing-feature-icon">
                  <TruckOutlined className="feature-icon" />
                </div>
                <Title level={4} className="landing-feature-title">
                  Gestion des livraisons
                </Title>
                <Paragraph className="landing-feature-text">
                  Attribuez les commandes aux livreurs, gérez les zones de livraison configurables.
                </Paragraph>
              </Card>
            </Col>

            <Col xs={24} md={8}>
              <Card className="landing-feature-card">
                <div className="landing-feature-icon">
                  <DashboardOutlined className="feature-icon" />
                </div>
                <Title level={4} className="landing-feature-title">
                  Analyses et statistiques
                </Title>
                <Paragraph className="landing-feature-text">
                  Tableau de bord avec statistiques globales.
                </Paragraph>
              </Card>
            </Col>
          </Row>
        </div>
      </section>

      {/* Integrations Section */}
      <section id="integrations" className="landing-integrations">
        <div className="landing-integrations-container">
          <Title level={3} className="landing-integrations-title">
            Intégrations Plug & Play
          </Title>
          <Paragraph className="landing-integrations-description">
            Connectez Mercuri Shop aux outils que vous utilisez déjà en quelques secondes. Aucun code requis.
          </Paragraph>

          <Row gutter={[40, 24]} justify="center">
            {[
              { name: 'Shopify', icon: <ShopOutlined /> },
              { name: 'WhatsApp Business', icon: <WhatsAppOutlined /> },
              { name: 'Gmail', icon: <ApiOutlined /> },
              { name: 'Point de Vente', icon: <MessageOutlined /> },
              { name: 'Analytics', icon: <BarChartOutlined /> }
            ].map((integration, index) => (
              <Col key={index}>
                <Card className="landing-integration-card">
                  <div className="landing-integration-icon">
                    {integration.icon}
                  </div>
                  <Text className="landing-integration-name">
                    {integration.name}
                  </Text>
                </Card>
              </Col>
            ))}
          </Row>
        </div>
      </section>

      {/* Testimonials Section avec étoiles modernisées */}
      <section id="temoignages" className="landing-testimonials">
        <div className="landing-testimonials-container">
          <div className="landing-testimonials-header">
            <Title level={2} className="landing-testimonials-title">
              Ce que disent nos clients
            </Title>
            <Paragraph className="landing-testimonials-description">
              Des entreprises de toutes tailles utilisent Mercuri Shop pour croître plus rapidement et satisfaire leurs clients.
            </Paragraph>
          </div>

          <Row gutter={[32, 32]}>
            {[
              {
                name: 'Franck Koffi',
                role: 'Propriétaire boutique ',
                avatar: `data:image/svg+xml;base64,${btoa(`
                  <svg width="48" height="48" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="24" cy="24" r="24" fill="#F59E0B"/>
                    <circle cx="24" cy="19" r="7" fill="white"/>
                    <path d="M10 38c0-9 6-14 14-14s14 5 14 14" fill="white"/>
                  </svg>
                `)}`,
                quote: 'Mercuri Shop a révolutionné notre gestion des commandes WhatsApp. Nos clients adorent recevoir les mises à jour automatiques et notre productivité a augmenté de 300%.'
              },
              {
                name: 'Moussa Traoré',
                role: 'Gérant e-commerce',
                avatar: `data:image/svg+xml;base64,${btoa(`
                  <svg width="48" height="48" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="24" cy="24" r="24" fill="#2563eb"/>
                    <circle cx="24" cy="19" r="7" fill="white"/>
                    <path d="M10 38c0-9 6-14 14-14s14 5 14 14" fill="white"/>
                  </svg>
                `)}`,
                quote: 'L\'intégration avec Shopify s\'est faite en quelques minutes. Maintenant nos clients reçoivent tout sur WhatsApp automatiquement. Un vrai game changer!'
              },
              {
                name: 'Nadia Adou',
                role: 'Entrepreneure',
                avatar: `data:image/svg+xml;base64,${btoa(`
                  <svg width="48" height="48" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="24" cy="24" r="24" fill="#0891b2"/>
                    <circle cx="24" cy="19" r="7" fill="white"/>
                    <path d="M10 38c0-9 6-14 14-14s14 5 14 14" fill="white"/>
                  </svg>
                `)}`,
                quote: 'Le système de livraison  et la gestion des livreurs nous fait gagner un temps précieux. Nos clients sont toujours informés en temps réel.'
              }
            ].map((testimonial, index) => (
              <Col xs={24} md={8} key={index}>
                <Card className="landing-testimonial-card">
                  <div className="landing-testimonial-author">
                    <Avatar 
                      size={48} 
                      src={testimonial.avatar}
                      className="landing-testimonial-avatar"
                    />
                    <div className="landing-testimonial-info">
                      <Text className="landing-testimonial-name">
                        {testimonial.name}
                      </Text>
                      <Text className="landing-testimonial-role">
                        {testimonial.role}
                      </Text>
                    </div>
                  </div>
                  
                  <Paragraph className="landing-testimonial-quote">
                    "{testimonial.quote}"
                  </Paragraph>
                  
                  {/* Étoiles modernisées */}
                  <div className="testimonial-stars">
                    {[...Array(5)].map((_, i) => (
                      <StarIcon key={i} className="h-4 w-4 text-yellow-400 fill-current" />
                    ))}
                  </div>
                </Card>
              </Col>
            ))}
          </Row>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="landing-pricing">
        <div className="landing-pricing-container">
          <div className="landing-pricing-header">
            <Title level={2} className="landing-pricing-title">
              Tarification simple et transparente
            </Title>
            <Paragraph className="landing-pricing-description">
              Tous les plans incluent les intégrations illimitées, l'automatisation et le support. Commencez gratuitement—évoluez quand vous grandissez.
            </Paragraph>
          </div>

          <Row gutter={[32, 32]} justify="center">
            {/* Plan Découverte - Gratuit */}
            <Col xs={24} md={6}>
              <Card className="landing-price-card">
                <div>
                  <Text className="landing-price-name" style={{ color: '#6B7280' }}>
                    Découverte
                  </Text>
                  
                  <div style={{ marginBottom: '20px' }}>
                    <Text className="landing-price-amount">
                      Gratuit
                    </Text>
                    <Text className="landing-price-period">
                      Pour toujours
                    </Text>
                  </div>

                  <div className="landing-price-features">
                    <Space direction="vertical" size={10}>
                      {[
                        '1 numéro WhatsApp',
                        '15 messages',
                        '5 produits maximum',
                        'Documentation'
                      ].map((feature, idx) => (
                        <div key={idx} className="landing-price-feature">
                          <CheckOutlined style={{ color: '#22C55E', fontSize: '12px' }} />
                          <Text className="landing-price-feature-text">{feature}</Text>
                        </div>
                      ))}
                    </Space>
                  </div>
                </div>

                <Link to="/register">
                  <Button 
                    block
                    style={{
                      backgroundColor: 'transparent',
                      borderColor: '#6B7280',
                      color: '#6B7280',
                      borderRadius: '8px',
                      height: '44px',
                      fontSize: '15px',
                      fontWeight: 600,
                      marginTop: '24px'
                    }}
                  >
                    Démarrer Gratuitement
                  </Button>
                </Link>
              </Card>
            </Col>

            {/* Plan Essentiel - Plus populaire */}
            <Col xs={24} md={6}>
              <Card className="landing-price-card popular" style={{ position: 'relative' }}>
                {/* Badge populaire */}
                <div className="landing-price-badge">
                  ⭐ PLUS POPULAIRE
                </div>
                
                <div style={{ marginTop: '8px' }}>
                  <Text className="landing-price-name" style={{ color: '#22C55E' }}>
                    Essentiel
                  </Text>
                  
                  <div style={{ marginBottom: '16px' }}>
                    <Text className="landing-price-amount">
                      9 900
                    </Text>
                    <Text style={{
                      fontSize: '16px',
                      fontWeight: 700,
                      color: '#6B7280'
                    }}>
                      {' '}XOF/mois
                    </Text>
                    <Text className="landing-price-save" style={{ color: '#22C55E' }}>
                      99 000 XOF/an (économisez 16%)
                    </Text>
                  </div>

                  <div className="landing-price-features">
                    <Space direction="vertical" size={10}>
                      {[
                        '1 numéro WhatsApp',
                        '300 messages/mois',
                        '200 produits',
                        'Import Shopify',
                        'Support email (48h)'
                      ].map((feature, idx) => (
                        <div key={idx} className="landing-price-feature">
                          <CheckOutlined style={{ color: '#22C55E', fontSize: '12px' }} />
                          <Text className="landing-price-feature-text">{feature}</Text>
                        </div>
                      ))}
                    </Space>
                  </div>
                </div>

                <Link to="/register">
                  <Button 
                    type="primary"
                    block
                    style={{
                      backgroundColor: '#22C55E',
                      borderColor: '#22C55E',
                      borderRadius: '8px',
                      height: '44px',
                      fontSize: '15px',
                      fontWeight: 600,
                      marginTop: '24px'
                    }}
                  >
                    Choisir Essentiel
                  </Button>
                </Link>
              </Card>
            </Col>

            {/* Plan Croissance */}
            <Col xs={24} md={6}>
              <Card className="landing-price-card">
                <div>
                  <Text className="landing-price-name" style={{ color: '#16A34A' }}>
                    Croissance
                  </Text>
                  
                  <div style={{ marginBottom: '20px' }}>
                    <Text className="landing-price-amount">
                      29 900
                    </Text>
                    <Text style={{
                      fontSize: '16px',
                      fontWeight: 700,
                      color: '#6B7280'
                    }}>
                      {' '}XOF/mois
                    </Text>
                    <Text className="landing-price-save" style={{ color: '#16A34A' }}>
                      299 000 XOF/an (économisez 16%)
                    </Text>
                  </div>

                  <div className="landing-price-features">
                    <Space direction="vertical" size={10}>
                      {[
                        '1 numéro WhatsApp',
                        '1 500 messages/mois',
                        'POS',
                        '10 livreurs'
                      ].map((feature, idx) => (
                        <div key={idx} className="landing-price-feature">
                          <CheckOutlined style={{ color: '#16A34A', fontSize: '12px' }} />
                          <Text className="landing-price-feature-text">{feature}</Text>
                        </div>
                      ))}
                    </Space>
                  </div>
                </div>

                <Link to="/register">
                  <Button 
                    block
                    style={{
                      backgroundColor: 'transparent',
                      borderColor: '#16A34A',
                      color: '#16A34A',
                      borderRadius: '8px',
                      height: '44px',
                      fontSize: '15px',
                      fontWeight: 600,
                      marginTop: '24px'
                    }}
                  >
                    Choisir Croissance
                  </Button>
                </Link>
              </Card>
            </Col>

            {/* Plan Excellence */}
            <Col xs={24} md={6}>
              <Card className="landing-price-card">
                <div>
                  <Text className="landing-price-name" style={{ color: '#7C3AED' }}>
                    Excellence
                  </Text>
                  
                  <div style={{ marginBottom: '20px' }}>
                    <Text style={{
                      fontSize: '28px',
                      fontWeight: 800,
                      color: '#111827'
                    }}>
                      Sur Devis
                    </Text>
                    <Text className="landing-price-period">
                      Solution sur mesure
                    </Text>
                  </div>

                  <div className="landing-price-features">
                    <Space direction="vertical" size={10}>
                      {[
                        '2 numéros WhatsApp',
                        'Messages illimités',
                        'Multi-boutiques',
                        'Support dédié 24/7'
                      ].map((feature, idx) => (
                        <div key={idx} className="landing-price-feature">
                          <CheckOutlined style={{ color: '#7C3AED', fontSize: '12px' }} />
                          <Text className="landing-price-feature-text">{feature}</Text>
                        </div>
                      ))}
                    </Space>
                  </div>
                </div>

                <Link to="/contact">
                  <Button 
                    block
                    style={{
                      backgroundColor: 'transparent',
                      borderColor: '#7C3AED',
                      color: '#7C3AED',
                      borderRadius: '8px',
                      height: '44px',
                      fontSize: '15px',
                      fontWeight: 600,
                      marginTop: '24px'
                    }}
                  >
                    Contacter les ventes
                  </Button>
                </Link>
              </Card>
            </Col>
          </Row>
        </div>
      </section>

      {/* CTA Section */}
      <section className="landing-cta">
        <div className="landing-cta-container">
          <Row gutter={[48, 32]} align="middle">
            <Col xs={24} md={12}>
              <Title level={2} className="landing-cta-title">
                Découvrez l'automatisation des commandes dès aujourd'hui !
              </Title>
              <Paragraph className="landing-cta-description">
                Commencez votre essai gratuit et voyez comment Mercuri Shop transforme votre business sur WhatsApp.
              </Paragraph>
            </Col>
            <Col xs={24} md={12}>
              <div className="landing-cta-buttons">
                <Link to="/register">
                  <Button 
                    size="large"
                    className="landing-cta-primary"
                  >
                    Essai gratuit 14 jours
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button 
                    size="large"
                    ghost
                    className="landing-cta-secondary"
                  >
                    Demander une démo
                  </Button>
                </Link>
              </div>
            </Col>
          </Row>
        </div>
      </section>

      {/* Footer */}
      <footer className="landing-footer">
        <div className="landing-footer-container">
          <Row justify="space-between" align="middle" className="landing-footer-top">
            <Col>
              <div className="landing-footer-logo">
                <div className="landing-footer-logo-icon">
                  <img src={logo} alt="Mercuri Shop" style={{ width: '20px', height: '20px', objectFit: 'contain' }} />
                </div>
                <Text className="landing-footer-logo-text">Mercuri Shop</Text>
              </div>
            </Col>
            
            <Col>
              <Space size={32} className="landing-footer-nav">
                <Link to="/privacy">
                  <Text className="landing-footer-nav-link">Politique de confidentialité</Text>
                </Link>
                <Link to="/terms">
                  <Text className="landing-footer-nav-link">Conditions d'utilisation</Text>
                </Link>
                <Link to="/contact">
                  <Text className="landing-footer-nav-link">Contact</Text>
                </Link>
              </Space>
            </Col>
            
            <Col>
              <Space size={12} className="landing-footer-social">
                <div className="landing-footer-social-icon">
                  <MessageOutlined style={{ fontSize: '14px', color: '#6B7280' }} />
                </div>
                <div className="landing-footer-social-icon">
                  <WhatsAppOutlined style={{ fontSize: '14px', color: '#6B7280' }} />
                </div>
                <div className="landing-footer-social-icon">
                  <BarChartOutlined style={{ fontSize: '14px', color: '#6B7280' }} />
                </div>
              </Space>
            </Col>
          </Row>
          
          <div className="landing-footer-bottom">
            <Text className="landing-footer-copyright">
              © 2024 Mercuri Shop. Tous droits réservés.
            </Text>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage; 